import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BiX } from "react-icons/bi";
import axios from "axios";
import { createProductType } from "../../redux/thunk";
import { Category } from "../../pages/WareHouseMgt/types";

interface AddCategoryModalProps {
  onClose: () => void;
  onCategoryAdded: (category: Category) => void;
}

export default function AddCategoryModal({
  onClose,
  onCategoryAdded,
}: AddCategoryModalProps) {
  const dispatch = useDispatch<any>();
  const { token } = useSelector((state: any) => state.auth);
  const [formData, setFormData] = useState({
    name: "",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setError(null);
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
      setError(null);
    }
  };

  const uploadImageAndGetUrl = async (file: File) => {
    const payload = new FormData();
    payload.append("file", file);
    payload.append("type", "image");

    const uploadUrl =
      process.env.REACT_APP_RESTAURANT_UPLOAD_URL ||
      (process.env.REACT_APP_RESTAURANT_URL
        ? `${process.env.REACT_APP_RESTAURANT_URL.replace(/\/v1\/api\/?$/, "").replace(/\/+$/, "")}/upload/file`
        : "https://api.dev.foodbank4u.com/restaurant/upload/file");

    const res = await axios.post(uploadUrl, payload, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: token,
      },
    });

    return res.data.url as string;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError("Please enter a category name.");
      return;
    }

    if (!imageFile) {
      setError("Please upload an image for the category.");
      return;
    }

    setLoading(true);
    try {
      const imageUrl = await uploadImageAndGetUrl(imageFile);

      if (!imageUrl) {
        setError("Image upload failed. Please try again.");
        setLoading(false);
        return;
      }

      const payload = {
        type: formData.name.trim(),
        image: imageUrl,
      };

      const result = await dispatch(createProductType(payload)).unwrap();

      if (result) {
        onCategoryAdded(result);
        onClose();
      }
    } catch (error: any) {
      console.error("Failed to add category:", error);
      setError(
        error?.response?.data?.message ||
          "Failed to add category. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">New Category</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <BiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <p className="text-sm text-red-500 mb-3">{error}</p>}
          <div className="space-y-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Category Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter category name"
                className="w-full px-3 py-2 border rounded-md"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter a unique name for the new category.
              </p>
            </div>

            <div>
              <label
                htmlFor="image"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Image
              </label>
              <input
                type="file"
                id="image"
                name="image"
                onChange={handleFileChange}
                accept="image/*"
                className="w-full px-3 py-2 border rounded-md"
              />
              <p className="text-xs text-gray-500 mt-1">
                Upload an image from your device.
              </p>
              {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md"
              disabled={loading}
            >
              Discard
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              disabled={loading}
            >
              {loading ? "Adding..." : "Add Category"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
